import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PrimaryButton from '../components/PrimaryButton';
import LeftPanel from '../components/LeftPanel';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Step from '../components/Step';
import { useRef } from 'react';

function OTP() {
    const [otp, setOtp] = useState(() => Array(6).fill(''));
    const location = useLocation();
    const { state } = location;
    const query = new URLSearchParams(location.search);
    const qpEmail = query.get('email') || '';
    const qpFlow = query.get('flow') || '';
    const [email, setEmail] = useState(state?.email || qpEmail || (() => { try { return localStorage.getItem('pendingEmail') || ''; } catch (e) { return ''; } })());
    const flow = state?.flow || qpFlow || null;
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [forgotStage, setForgotStage] = useState(flow === 'forgot-password' ? 'verify' : null); // 'verify' | 'reset'
    const [successMessage, setSuccessMessage] = useState('');
    const [touched, setTouched] = useState({ otp: false });
    const [serverError, setServerError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const inputsRef = useRef([]);
    const newPasswordRef = useRef(null);

    const toggleShowNewPassword = () => setShowNewPassword((s) => !s);
    const toggleShowConfirmPassword = () => setShowConfirmPassword((s) => !s);

    function handleSubmitAuto(otpValue) {
        // otpValue optional - if omitted, use current state
        const otpStr = otpValue || otp.join('');
        if (otpStr.trim().length !== 6) return;
        (async () => {
            setSubmitting(true);
            setServerError('');
            setFieldErrors({});
            try {
                if (flow === 'forgot-password') {
                    if (forgotStage === 'verify') {
                        // Verify OTP first
                        const res = await api.post('/api/auth/verify-otp', null, { params: { email, otp: otpStr, consume: false } });
                        const data = res?.data;
                        if (data === 'OTP verified' || (data && data.success)) {
                            // move to reset stage to show password inputs
                            setForgotStage('reset');
                            setServerError('OTP verified — enter your new password');
                            setTimeout(() => { try { newPasswordRef.current && newPasswordRef.current.focus(); } catch (e) { } }, 50);
                            return;
                        }
                        if (data && data.fieldErrors) {
                            setFieldErrors(data.fieldErrors);
                            return;
                        }
                        setServerError(data?.message || 'Invalid OTP');
                    } else {
                        // in reset stage, do not auto-submit reset — wait for user to enter passwords
                        return;
                    }
                } else {
                    const res = await api.post('/api/auth/verify-otp', null, { params: { email, otp: otpStr } });
                    const data = res?.data;
                    if (data === 'OTP verified' || (data && data.success)) {
                        try { localStorage.removeItem('pendingEmail'); } catch (e) { }
                        navigate('/login');
                        return;
                    }
                    if (data && data.fieldErrors) {
                        setFieldErrors(data.fieldErrors);
                        return;
                    }
                    setServerError(data?.message || 'Invalid OTP');
                }
            } catch (err) {
                const data = err?.response?.data;
                if (data?.fieldErrors) setFieldErrors(data.fieldErrors);
                else if (data?.message) setServerError(data.message);
                else setServerError('OTP verification failed. Please try again.');
            } finally {
                setSubmitting(false);
            }
        })();
    }

    function OtpBox({ index }) {
        const value = otp[index] || '';

        const handleChangeBox = (e) => {
            let val = e.target.value || '';
            // keep only last digit if user pasted or typed multiple
            if (val.length > 1) val = val.charAt(val.length - 1);
            // only digits
            if (val && !/^[0-9]$/.test(val)) return;

            // Build new otp array and set state
            const next = [...otp];
            next[index] = val;
            setOtp(next);

            // If a digit was entered, move focus to the next visible input
            if (val && index < 5) {
                setTimeout(() => {
                    try {
                        const visibles = Array.from(document.querySelectorAll('input[type="text"]')).filter(i => i.offsetParent !== null);
                        const nextEl = visibles[index + 1];
                        if (nextEl) { nextEl.focus(); nextEl.select && nextEl.select(); }
                    } catch (err) { }
                }, 0);
            }

            // If all 6 digits are filled, auto-submit
            if (next.join('').trim().length === 6) {
                setTimeout(() => handleSubmitAuto(next.join('')), 50);
            }
        };

        const handleKeyDown = (e) => {
            // Backspace/Delete: clear current if present; if empty move to previous and clear it
            if (e.key === 'Backspace' || e.key === 'Delete') {
                e.preventDefault();
                if (value) {
                    // clear current only
                    setOtp((prev) => {
                        const next = [...prev];
                        next[index] = '';
                        return next;
                    });
                    // keep focus here
                    setTimeout(() => {
                        try {
                            const visibles = Array.from(document.querySelectorAll('input[type="text"]')).filter(i => i.offsetParent !== null);
                            const cur = visibles[index];
                            if (cur) { cur.focus(); cur.select && cur.select(); }
                        } catch (err) { }
                    }, 0);
                } else if (index > 0) {
                    // move focus to previous and clear it
                    setOtp((prev) => {
                        const next = [...prev];
                        next[index - 1] = '';
                        return next;
                    });
                    setTimeout(() => {
                        try {
                            const visibles = Array.from(document.querySelectorAll('input[type="text"]')).filter(i => i.offsetParent !== null);
                            const prev = visibles[index - 1];
                            if (prev) { prev.focus(); prev.select && prev.select(); }
                        } catch (err) { }
                    }, 0);
                }
                return;
            }
            if (e.key === 'ArrowLeft') {
                const prevInput = inputsRef.current[index - 1];
                if (prevInput) setTimeout(() => prevInput.focus(), 0);
            } else if (e.key === 'ArrowRight') {
                const next = inputsRef.current[index + 1];
                if (next) setTimeout(() => next.focus(), 0);
            }
        };

        const handlePaste = (e) => {
            e.preventDefault();
            const pasted = e.clipboardData.getData('Text').replace(/\D/g, '');
            if (!pasted) return;
            // If pasted a full 6-digit code, set all and focus last
            if (/^\d{6}$/.test(pasted)) {
                const newDigits = pasted.split('');
                setOtp(newDigits);
                const last = inputsRef.current[5];
                if (last) setTimeout(() => last.focus(), 0);
                setTimeout(() => handleSubmitAuto(pasted), 50);
                return;
            }
            // Otherwise fill sequentially from current index
            const next = [...otp];
            for (let i = 0; i < pasted.length && index + i < 6; i++) {
                next[index + i] = pasted[i];
            }
            setOtp(next);
            const focusIndex = Math.min(5, index + pasted.length);
            setTimeout(() => {
                try {
                    const visibles = Array.from(document.querySelectorAll('input[type="text"]')).filter(i => i.offsetParent !== null);
                    const focusEl = visibles[focusIndex];
                    if (focusEl) focusEl.focus();
                } catch (err) { }
            }, 0);
        };

        return (
            <TextField
                inputProps={{ maxLength: 1 }}
                value={value}
                onChange={handleChangeBox}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                    const el = inputsRef.current[index];
                    try { el && el.select && el.select(); } catch (e) { }
                }}
                onPaste={handlePaste}
                inputRef={(el) => (inputsRef.current[index] = el)}
                sx={{
                    width: 56,
                    '& .MuiInputBase-root': { height: 56 },
                    '& .MuiInputBase-input': { textAlign: 'center', p: 0, fontSize: 20, lineHeight: '56px' },
                }}
                variant="outlined"
                size="small"
            />
        );
    }

    const handleBlur = (field) => setTouched((t) => ({ ...t, [field]: true }));

    const validation = {
        otp: otp.join('').trim().length === 6,
        newPassword: forgotStage === 'reset' ? newPassword && newPassword.length >= 8 : true,
        confirmPassword: forgotStage === 'reset' ? confirmPassword === newPassword : true,
    };
    const formValid = Object.values(validation).every(Boolean);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setTouched({ otp: true });
        if (!formValid) return;
        try {
            setSubmitting(true);
            setServerError('');
            setFieldErrors({});
            if (flow === 'forgot-password') {
                if (forgotStage === 'verify') {
                    // verify OTP (manual submit)
                    const res = await api.post('/api/auth/verify-otp', null, { params: { email, otp: otp.join(''), consume: false } });
                    const data = res?.data;
                    if (data === 'OTP verified' || (data && data.success)) {
                        setForgotStage('reset');
                        setServerError('OTP verified — enter your new password');
                        setTimeout(() => { try { newPasswordRef.current && newPasswordRef.current.focus(); } catch (e) { } }, 50);
                        return;
                    }
                    if (data && data.fieldErrors) {
                        setFieldErrors(data.fieldErrors);
                        return;
                    }
                    setServerError(data?.message || 'Invalid OTP');
                } else {
                    // reset stage: validate and call reset-password
                    if (!newPassword || newPassword.length < 8) {
                        setServerError('Please enter a new password at least 8 characters long');
                        return;
                    }
                    if (confirmPassword !== newPassword) {
                        setServerError('Passwords do not match');
                        return;
                    }
                    const res = await api.post('/api/auth/reset-password', null, { params: { email, otp: otp.join(''), newPassword } });
                    const data = res?.data;
                    if (typeof data === 'string' && data.toLowerCase().includes('success')) {
                        // show success message and redirect shortly
                        setSuccessMessage('Your password has been updated. Redirecting to login...');
                        setTimeout(() => { try { localStorage.removeItem('pendingEmail'); } catch (e) { } navigate('/login'); }, 1800);
                        return;
                    }
                    if (data && data.fieldErrors) {
                        setFieldErrors(data.fieldErrors);
                        return;
                    }
                    setServerError(data?.message || 'Invalid OTP or reset failed');
                }
            } else {
                const res = await api.post('/api/auth/verify-otp', null, { params: { email, otp: otp.join('') } });
                const data = res?.data;
                if (data === 'OTP verified' || (data && data.success)) {
                    try { localStorage.removeItem('pendingEmail'); } catch (e) { }
                    navigate('/login');
                    return;
                }
                if (data && data.fieldErrors) {
                    setFieldErrors(data.fieldErrors);
                    return;
                }
                setServerError(data?.message || 'Invalid OTP');
            }
        } catch (err) {
            const data = err?.response?.data;
            if (data?.fieldErrors) setFieldErrors(data.fieldErrors);
            else if (data?.message) setServerError(data.message);
            else setServerError('OTP verification failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box component="main">
            <Grid container spacing={4} alignItems="flex-start" sx={{ mt: 2 }}>
                <Grid item xs={12} md={4} lg={3}>
                    <LeftPanel items={[
                        <Step key="step1">1. Enter your code</Step>,
                        'Use the one-time code sent to your email.',

                        <Step key="step2">2. Need a new OTP code?</Step>,
                        'Request a new code below if it expired or didn’t arrive.',

                        <Step key="step3">3. Verify & continue</Step>,
                        'We’ll confirm your account.',

                        <Step key="step4">4. Next steps</Step>,
                        'Log in, invite collaborators, or continue your application.'
                    ]}>
                        {!email && (
                            <TextField
                                label="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                                sx={{ mb: 1, bgcolor: '#fff', borderRadius: 1 }}
                            />
                        )}
                        <PrimaryButton
                            type="button"
                            onClick={async () => {
                                if (!email) {
                                    setServerError('No email found to request a new code.');
                                    return;
                                }
                                try {
                                    setSubmitting(true);
                                    setServerError('');
                                    await api.post('/api/auth/resend-otp', null, {
                                        params: {
                                            email,
                                            ...(flow === 'forgot-password' ? { flow: 'forgot-password' } : {}),
                                        },
                                    });
                                    try { localStorage.setItem('pendingEmail', email); } catch (e) { }
                                    setServerError('A new code has been requested. Check your email.');
                                } catch (err) {
                                    setServerError(err?.response?.data || 'Failed to request new code');
                                } finally {
                                    setSubmitting(false);
                                }
                            }}
                            disabled={submitting}
                            sx={{ bgcolor: '#fff', color: '#000', textTransform: 'none' }}
                        >
                            Request New OTP Code
                        </PrimaryButton>
                    </LeftPanel>
                </Grid>

                <Grid item xs={12} md={8} lg={6}>
                    <Paper elevation={1} sx={{ p: 0, overflow: 'hidden' }}>
                        <Box sx={{ bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', px: 3, py: { xs: 1.5, md: 2 } }}>
                            <Box
                                component="img"
                                src="/ryslogo.png"
                                alt="Retool Your School"
                                sx={{ height: { xs: 42, md: 60 }, objectFit: 'contain' }}
                            />
                        </Box>
                        <Box sx={{ p: { xs: 3, md: 4 } }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                                Verify Your Account
                            </Typography>
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>Email: {email}</Typography>

                            {serverError ? <Alert severity="info" sx={{ mb: 2 }}>{serverError}</Alert> : null}
                            {successMessage ? <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert> : null}

                            <Box component="form" onSubmit={handleSubmit} noValidate>
                                <Grid container spacing={2}>
                                    {!(flow === 'forgot-password' && forgotStage === 'reset') && (
                                        <Grid item xs={12}>
                                            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 1 }}>
                                                {Array.from({ length: 6 }).map((_, i) => (
                                                    <OtpBox key={i} index={i} />
                                                ))}
                                            </Stack>
                                        </Grid>
                                    )}
                                    {flow === 'forgot-password' && forgotStage === 'reset' && (
                                        <>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="New Password"
                                                    type={showNewPassword ? 'text' : 'password'}
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    onBlur={() => handleBlur('newPassword')}
                                                    error={Boolean(fieldErrors.newPassword) || (touched.newPassword && !validation.newPassword)}
                                                    helperText={fieldErrors.newPassword || (touched.newPassword && !validation.newPassword ? 'Password must be at least 8 characters' : '')}
                                                    fullWidth
                                                    required
                                                    inputRef={newPasswordRef}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                                                                    onClick={toggleShowNewPassword}
                                                                    edge="end"
                                                                    size="large"
                                                                >
                                                                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Confirm Password"
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    onBlur={() => handleBlur('confirmPassword')}
                                                    error={Boolean(fieldErrors.confirmPassword) || (touched.confirmPassword && !validation.confirmPassword)}
                                                    helperText={fieldErrors.confirmPassword || (touched.confirmPassword && !validation.confirmPassword ? 'Passwords must match' : '')}
                                                    fullWidth
                                                    required
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                                                    onClick={toggleShowConfirmPassword}
                                                                    edge="end"
                                                                    size="large"
                                                                >
                                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                            <PrimaryButton type="submit" disabled={!formValid || submitting}>
                                                {submitting ? (forgotStage === 'reset' ? 'Resetting...' : 'Verifying...') : (flow === 'forgot-password' && forgotStage === 'reset' ? 'Reset Password' : 'Verify Code')}
                                            </PrimaryButton>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                            <Box sx={{ textAlign: 'left', maxWidth: 440 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Didn’t receive your code?</Typography>
                                                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                                    <li>Check your Junk or Spam folder</li>
                                                    <li>Confirm your email address is correct</li>
                                                    <li>Click the “Request New OTP Code” button to try again</li>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default OTP;
